import React, {
  createContext,
  useCallback,
  useReducer,
  useRef,
  useState
} from 'react';
import useTranslations from '../../../hooks/useTranslations';
import AutomationWizardView from '../view/AutomationWizardView';

export const AutomationsWizardContext = createContext({});

const validateActions = actions => {
  let results = false;

  actions.forEach(a => {
    switch (a.actionType) {
      case 'empty':
        break;
      case 'fieldupdate':
        if (
          a.additionalParameters &&
          a.additionalParameters.statusData &&
          !!a.additionalParameters.statusData.status
        ) {
          results = true;
        }
        break;
      case 'assign_to_admin':
        if (a.additionalParameters && !!a.additionalParameters.assigneeId) {
          results = true;
        }
        break;
      case 'email':
        const root = a.additionalParameters && a.additionalParameters.root;

        if (root && root.templateId && root.to) {
          results = true;
        }
        break;
      default:
        break;
    }
  });

  return results;
};

const WizardReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return {
        ...state,
        automationData: { ...state.automationData, ...payload }
      };

    case 'update_rule':
      return {
        ...state,
        automationData: {
          ...state.automationData,
          rule: { ...state.automationData.rule, ...payload }
        }
      };
    case 'update_promise':
      return { ...state, schedulerPromise: payload };
    default:
      return state;
  }
};

const AutomationWizardEngine = ({
  onWizardClose = () => {},
  automationData = {},
  isTitleValid = title => true,
  schedulerData = {}
}) => {
  const t = useTranslations();
  const [data, dispatch] = useReducer(WizardReducer, {
    automationData,
    schedulerPromise: () => {}
  });
  const [titleError, setTitleError] = useState(false);
  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] =
    useState(false);
  const actionsAPI = useRef();
  const dirtyList = useRef({
    title: false,
    trigger: false,
    conditions: false,
    actions: false
  });

  const onTitleChange = useCallback(
    event => {
      const title = event.target.value;
      dispatch({ type: 'update', payload: { label: title } });

      setTitleError(isTitleValid(title, data.automationData.position));

      dirtyList.current.title =
        (t(title) || '').trim() !== (t(automationData.label) || '').trim();
    },
    [data, isTitleValid]
  );

  const onActionsChange = useCallback(actions => {
    // action is legacy
    dispatch({
      type: 'update_rule',
      payload: { actionsList: actions, action: {} }
    });

    dirtyList.current.actions = true;
    if (automationData.rule.action && actions.length === 1) {
      if (
        JSON.stringify(actions[0]) ===
        JSON.stringify(automationData.rule.action)
      ) {
        dirtyList.current.actions = false;
      }
    }
    if (
      JSON.stringify(actions) ===
      JSON.stringify(automationData.rule.actionsList)
    ) {
      dirtyList.current.actions = false;
    }
  }, []);

  const onCancel = useCallback(() => {
    if (Object.values(dirtyList.current).every(b => !b)) {
      onWizardClose();
      return;
    }
    setIsDiscardChangesDialogOpen(true);
  }, [data, onWizardClose]);

  const onSave = useCallback(() => {
    onWizardClose(data.automationData, data.schedulerPromise);
  }, [data, onWizardClose]);

  const isSaveBtnDisabled = useCallback(() => {
    const automation = data.automationData;

    const doesActionsExist =
      automation.rule &&
      ((!!automation.rule.action &&
        !!Object.keys(automation.rule.action).length) ||
        (automation.rule.actionsList &&
          !!automation.rule.actionsList.length &&
          automation.rule.actionsList.some(a => !a.disabled)));

    // If action exist we know it's valid because it's old and valid code that is being replaced by actionsList.
    // Therfore if it exist we know we didn't change anything around the actions area and it's valid.
    const areRulesValid =
      automation.rule &&
      (!!automation.rule.action ||
        (automation.rule.actionsList.some(a => a.actionType !== 'empty') &&
          validateActions(automation.rule.actionsList)));

    const isTriggerValid =
      automation.rule &&
      automation.rule.when &&
      !!automation.rule.when.eventType;

    const isTitleValid = !titleError && !!automation.label;

    return !(
      isTitleValid &&
      doesActionsExist &&
      areRulesValid &&
      isTriggerValid
    );
  }, [titleError, data]);

  const onLogicChange = useCallback(query => {
    dispatch({
      type: 'update_rule',
      payload: { condition: query }
    });

    dirtyList.current.conditions = true;
    if (automationData.rule.condition === query) {
      dirtyList.current.conditions = false;
    }
  }, []);

  const onTriggerChange = useCallback(
    (trigger, info) => {
      if (actionsAPI && actionsAPI.current) {
        actionsAPI.current.updateActionsListBasedOnTriggerChange(trigger);
      }

      dispatch({
        type: 'update_rule',
        payload: {
          when: { eventType: trigger, eventInfo: info }
        }
      });
      dirtyList.current.conditions = true;
      if (automationData.rule.when.eventType === trigger) {
        dirtyList.current.conditions = false;
      }
    },
    [data]
  );

  const contextProps = {
    automationData: data.automationData,
    actionsAPI
  };

  const viewProps = {
    onCancel,
    data: data.automationData,
    dispatch,
    onTitleChange,
    onActionsChange,
    titleError,
    isDiscardChangesDialogOpen,
    setIsDiscardChangesDialogOpen,
    onWizardClose,
    isSaveBtnDisabled,
    onLogicChange,
    onTriggerChange,
    onSave,
    schedulerData
  };

  return (
    <AutomationsWizardContext.Provider value={contextProps}>
      <AutomationWizardView {...viewProps} />
    </AutomationsWizardContext.Provider>
  );
};

export default AutomationWizardEngine;
